import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dealService } from '../../services/dealService';
import { extractApiError } from '../../services/apiClient';

export const RatingScreen: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!stars || !dealId) return;
    setIsLoading(true);
    setError('');
    try {
      await dealService.createRating({ deal_id: dealId, stars, comment: comment.trim() || undefined });
      navigate(-1);
    } catch (err: any) {
      setError(extractApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const labels = ['', 'Could be better', 'Fair', 'Good', 'Very good', 'Excellent!'];

  return (
    <div style={s.page}>
      <div style={s.card}>
        <h2 style={s.title}>Rate Your Experience</h2>
        <p style={s.subtitle}>How was your experience with this service?</p>

        {error && <div style={s.errorBox}>{error}</div>}

        <div style={s.starsRow}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setStars(n)} style={s.starBtn}>
              <span style={{ fontSize: 36, color: n <= stars ? '#F59E0B' : '#E0E0E0', transition: 'color 0.15s' }}>★</span>
            </button>
          ))}
        </div>
        <p style={s.ratingLabel}>{stars === 0 ? 'Tap a star to rate' : labels[stars]}</p>

        <label style={s.label}>Comment (Optional)</label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Share details of your experience..."
          maxLength={500}
          rows={4}
          style={s.textarea}
        />
        <div style={s.charCount}>{comment.length}/500</div>

        <div style={s.actions}>
          <button onClick={() => navigate(-1)} style={s.skipBtn}>Skip</button>
          <button onClick={handleSubmit} disabled={stars === 0 || isLoading} style={{ ...s.submitBtn, opacity: stars === 0 ? 0.5 : 1 }}>
            {isLoading ? 'Submitting...' : 'Submit Rating'}
          </button>
        </div>
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 520, margin: '0 auto' },
  card: { background: '#FFF', borderRadius: 16, padding: '40px', border: '1px solid #E8ECF0', boxShadow: '0 4px 16px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.02)', textAlign: 'center' as const },
  title: { fontSize: 22, fontWeight: 700, color: '#2D3436', margin: '0 0 4px' },
  subtitle: { fontSize: 14, color: '#636E72', margin: '0 0 24px' },
  errorBox: { background: '#FEE2E2', color: '#DC2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16, textAlign: 'left' as const },
  starsRow: { display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 8 },
  starBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 4 },
  ratingLabel: { fontSize: 14, fontWeight: 600, color: '#636E72', marginBottom: 24 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#2D3436', marginBottom: 8, textAlign: 'left' as const },
  textarea: { width: '100%', padding: 14, border: '1px solid #E8ECF0', borderRadius: 10, fontSize: 14, fontFamily: "'Inter', sans-serif", resize: 'vertical' as const, outline: 'none', boxSizing: 'border-box' as const },
  charCount: { fontSize: 11, color: '#B0B8C1', textAlign: 'right' as const, marginTop: 4 },
  actions: { display: 'flex', gap: 12, marginTop: 24 },
  skipBtn: { flex: 1, padding: 14, background: '#F5F6FA', border: '1px solid #E8ECF0', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', color: '#636E72' },
  submitBtn: { flex: 2, padding: 14, background: '#0C5ADB', color: '#FFF', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'opacity 0.2s' },
};
